import { useEffect, useState } from 'react';
import { StyledTable } from '../../components';
import { connect } from 'react-redux';
import { fetchStationUpcomingParts, fetchStationTabUpcomingParts } from '../../redux/actionCreators/station';
import { getFeatures } from '../../redux/actionCreators/processValidation';
import {
	ADDITIONAL_COLUMNS_FOR_CHASSIS_LINE,
	CHASIS_LINE_IDS,
	DEFAULT_COLUMNS as COLUMNS,
	COLUMNS_TO_HIDE_FOR_CHASSIS_LINE,
	OPERATIONS,
	PART_VALIDATION_ERROR,
	isPuneJlr,
} from '../../constants';
import { Box, Typography, ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import PartScanningDetailsPopover from '../../components/PartscanningDetailsPopover/PartScanningDetailsPopover';
import TableActionsBar from '../../components/TableActionBar/TableActionBar';
import { DARK_MODE } from '../../constants';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ScheduleIcon from '@mui/icons-material/Schedule';

// ---------------------------------------------------------------------------
// Tab card styles — sharp, flat, accent-bar design
// ---------------------------------------------------------------------------
const TabStrip = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'stretch',
	gap: '4px',
	flexShrink: 0,
}));

const TabCard = styled(ButtonBase)(({ theme, active, isdisabled }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	justifyContent: 'center',
	position: 'relative',
	padding: '10px 20px 10px 16px',
	minWidth: '160px',
	borderRadius: 0,
	overflow: 'hidden',
	cursor: isdisabled ? 'not-allowed' : 'pointer',
	transition: 'background 0.2s ease',

	background: active
		? theme.palette.mode === DARK_MODE
			? 'rgba(107, 161, 229, 0.12)'
			: 'rgba(107, 161, 229, 0.1)'
		: theme.palette.mode === DARK_MODE
			? 'rgba(255,255,255,0.03)'
			: 'rgba(15,16,123,0.06)',

	border: active
		? `1px solid ${theme.palette.mode === DARK_MODE ? 'rgba(107,161,229,0.3)' : 'rgba(107,161,229,0.4)'}`
		: '1px solid rgba(255,255,255,0.08)',

	opacity: isdisabled ? 0.38 : 1,

	// Top accent stripe
	'&::before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		height: '3px',
		background: active
			? 'linear-gradient(90deg, #6BA1E5 0%, #8385E4 100%)'
			: 'transparent',
		transition: 'background 0.2s ease',
	},

	[theme.breakpoints.down('sm')]: { minWidth: '130px', padding: '8px 14px 8px 12px' },
	[theme.breakpoints.up('xl')]: { minWidth: '190px', padding: '13px 24px 13px 20px' },
}));

const TabIcon = styled(Box)(({ active }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	marginBottom: '4px',
	opacity: active ? 1 : 0.5,
	transition: 'opacity 0.2s ease',
	color: active ? '#6BA1E5' : '#FFFFFF',
}));

const TabLabel = styled(Typography)(({ active }) => ({
	fontFamily: "'JLR Emeric', sans-serif",
	fontWeight: active ? 700 : 500,
	fontSize: '13px',
	letterSpacing: '0.04em',
	textTransform: 'uppercase',
	color: active ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
	lineHeight: 1,
	transition: 'color 0.2s ease, font-weight 0.2s ease',
}));

const TabCount = styled(Box)(({ active }) => ({
	marginTop: '5px',
	display: 'inline-flex',
	alignItems: 'center',
	gap: '4px',
}));

const CountNum = styled(Typography)(({ active }) => ({
	fontFamily: "'JLR Emeric', sans-serif",
	fontWeight: 800,
	fontSize: '22px',
	lineHeight: 1,
	color: active ? '#FFFFFF' : 'rgba(255,255,255,0.25)',
	transition: 'color 0.2s ease',
}));

const CountLabel = styled(Typography)(({ active }) => ({
	fontFamily: "'JLR Emeric', sans-serif",
	fontWeight: 500,
	fontSize: '11px',
	color: active ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.2)',
	lineHeight: 1,
	textTransform: 'uppercase',
	letterSpacing: '0.05em',
	alignSelf: 'flex-end',
	paddingBottom: '2px',
}));

// ---------------------------------------------------------------------------
// Redux wiring
// ---------------------------------------------------------------------------
const mapStateToProps = (state) => ({
	allowedOperations: state.operations.allowedOperations,
	user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
	fetchStationUpcomingParts: (successCallback, errorCallback) =>
		dispatch(fetchStationUpcomingParts(successCallback, errorCallback)),
	fetchStationTabUpcomingParts: (successCallback, errorCallback) =>
		dispatch(fetchStationTabUpcomingParts(successCallback, errorCallback)),
	getFeatures: (success, error) => dispatch(getFeatures(success, error)),
});

// ---------------------------------------------------------------------------
// Tab constants
// ---------------------------------------------------------------------------
const TAB_ALL_VINS = 0;
const TAB_UPCOMING_VINS = 1;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const UpcomingParts = ({
	fetchStationUpcomingParts,
	fetchStationTabUpcomingParts,
	getFeatures,
	allowedOperations,
	user,
}) => {
	const { showMessageOverlay } = useMessageContextManager();

	const [isDisplayOverallParamsFalse, setIsDisplayOverallParamsFalse] = useState(false);
	const [activeTab, setActiveTab] = useState(TAB_ALL_VINS);
	const [allVinsItems, setAllVinsItems] = useState([]);
	const [upcomingVinsItems, setUpcomingVinsItems] = useState([]);
	const [filters, setFilters] = useState({});
	const [columns, setColumns] = useState([]);

	// ---------------------------------------------------------------------------
	// Column definitions
	// ---------------------------------------------------------------------------
	const ADDITIONAL_DEFAULT_COLUMNS = [
		{
			width: 100,
			title: 'Line Up',
			field: 'lineUp',
			sortable: true,
			render: (row) => (
				<Typography
					sx={{
						opacity: '80%',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						textAlign: 'center',
					}}
				>
					{row?.index + 1}
				</Typography>
			),
		},
		{
			width: 400,
			title: 'VIN',
			field: 'serialNumber',
			enableSearch: true,
			enableFilter: true,
			sortable: true,
			align: 'left',
			render: (row) => (
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '90%' }}>
					<Typography
						sx={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}
					>
						{row.serialNumber ? row.serialNumber : '-'}
					</Typography>
				</Box>
			),
		},
		{
			width: 160,
			title: 'Status',
			field: 'status',
			render: (row) =>
				row.serialNumber ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
						<PartScanningDetailsPopover rowData={row} />
					</Box>
				) : null,
		},
		{
			title: 'KESPEC NUMBER',
			field: 'partNumber',
			width: 160,
			sortable: true,
			render: (row) => (
				<Typography sx={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'center' }}>
					{row?.partNumber}
				</Typography>
			),
		},
		{
			title: isPuneJlr() ? 'MODEL' : 'KESPEC DESCRIPTION',
			field: isPuneJlr() ? 'model' : 'description',
			width: 200,
			sortable: true,
			align: 'left',
			render: (row) => (
				<Typography sx={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
					{isPuneJlr() ? row?.model : row?.description}
				</Typography>
			),
		},
		{
			title: isPuneJlr() ? 'MODEL YEAR' : 'DATE',
			field: isPuneJlr() ? 'modelYear' : 'productionDt',
			width: 140,
			sortable: true,
			render: (row) => (
				<Typography sx={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'center' }}>
					{isPuneJlr() ? row?.modelYear : row?.productionDt}
				</Typography>
			),
		},
	];

	useEffect(() => {
		setColumns(ADDITIONAL_DEFAULT_COLUMNS);
	}, []);

	// ---------------------------------------------------------------------------
	// Data mapper
	// ---------------------------------------------------------------------------
	const mapLineItems = (data) =>
		data.map((element, index) => ({
			...element,
			id: element.sequenceNumber,
			readyForDropSequenceNumber: element.metaData?.readyForDropSequenceNumber,
			vcDescription: element.metaData?.vcDescription,
			vcPartNumber: element.metaData?.vcPartNumber,
			vcSequenceNumber: element.metaData?.vcSequenceNumber,
			vcNumber: element.metaData?.vcNumber,
			shiftLabel: element.metaData?.shiftLabel,
			majorModel: element.metaData?.majormodel,
			country: element.metaData?.country,
			isVinScannedOnce: false,
			index,
		}));

	// ---------------------------------------------------------------------------
	// API calls
	// ---------------------------------------------------------------------------
	const fetchAllVins = () => {
		fetchStationUpcomingParts(
			(data) => setAllVinsItems(mapLineItems(data)),
			(error) => showMessageOverlay({ contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'All VINs', info: error?.response?.data?.message, error: true }),
		);
	};

	const fetchUpcomingVins = () => {
		fetchStationTabUpcomingParts(
			(data) => {
				const dataArray = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
				setUpcomingVinsItems(mapLineItems(dataArray));
			},
			(error) => showMessageOverlay({ contextText: PART_VALIDATION_ERROR.ERROR_CONTEXT_TEXT + 'Upcoming VINs', info: error?.response?.data?.message || 'Failed to fetch upcoming VINs', error: true }),
		);
	};

	// ---------------------------------------------------------------------------
	// Mount: check feature flag → load initial tab
	// ---------------------------------------------------------------------------
	useEffect(() => {
		getFeatures(
			(data) => {
				const flag = data?.find((f) => f.name === 'DISPLAY_OVERALL_PARAMS');
				const isParamsFalse = flag?.enabled === false;
				setIsDisplayOverallParamsFalse(isParamsFalse);
				if (isParamsFalse) {
					setActiveTab(TAB_UPCOMING_VINS);
					fetchUpcomingVins();
				} else {
					fetchAllVins();
				}
			},
			() => fetchAllVins(),
		);
	}, []);

	// ---------------------------------------------------------------------------
	// Tab switch
	// ---------------------------------------------------------------------------
	const handleTabChange = (newValue) => {
		if (newValue === activeTab) return;
		setActiveTab(newValue);
		setFilters({});
		if (newValue === TAB_UPCOMING_VINS && upcomingVinsItems.length === 0) fetchUpcomingVins();
		if (newValue === TAB_ALL_VINS && allVinsItems.length === 0) fetchAllVins();
	};

	const activeRows = activeTab === TAB_ALL_VINS ? allVinsItems : upcomingVinsItems;
	const allActive = activeTab === TAB_ALL_VINS;
	const upcomingActive = activeTab === TAB_UPCOMING_VINS;

	return (
		<>
			{/* ── Header row ───────────────────────────────────────────────── */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-end',
					justifyContent: 'space-between',
					mb: 1.5,
					gap: 2,
					flexWrap: 'wrap',
				}}
			>
				{/* Tab cards */}
				<TabStrip>
					{/* All VINs */}
					<TabCard
						active={allActive ? 1 : 0}
						isdisabled={isDisplayOverallParamsFalse ? 1 : 0}
						onClick={() => !isDisplayOverallParamsFalse && handleTabChange(TAB_ALL_VINS)}
						disableRipple={isDisplayOverallParamsFalse}
					>
						<TabIcon active={allActive ? 1 : 0}>
							<FormatListBulletedIcon sx={{ fontSize: 16 }} />
							<TabLabel active={allActive ? 1 : 0}>All VINs</TabLabel>
						</TabIcon>
						<TabCount active={allActive ? 1 : 0}>
							<CountNum active={allActive ? 1 : 0}>
								{allVinsItems.length}
							</CountNum>
							<CountLabel active={allActive ? 1 : 0}>records</CountLabel>
						</TabCount>
					</TabCard>

					{/* Divider */}
					<Box sx={{ width: '1px', background: 'rgba(255,255,255,0.08)', alignSelf: 'stretch', mx: '1px' }} />

					{/* Upcoming VINs */}
					<TabCard
						active={upcomingActive ? 1 : 0}
						isdisabled={0}
						onClick={() => handleTabChange(TAB_UPCOMING_VINS)}
					>
						<TabIcon active={upcomingActive ? 1 : 0}>
							<ScheduleIcon sx={{ fontSize: 16 }} />
							<TabLabel active={upcomingActive ? 1 : 0}>Upcoming VINs</TabLabel>
						</TabIcon>
						<TabCount active={upcomingActive ? 1 : 0}>
							<CountNum active={upcomingActive ? 1 : 0}>
								{upcomingVinsItems.length}
							</CountNum>
							<CountLabel active={upcomingActive ? 1 : 0}>records</CountLabel>
						</TabCount>
					</TabCard>
				</TabStrip>

				{/* Filter / Columns */}
				<TableActionsBar
					showColumns
					showFilter
					columns={columns}
					defaultColumns={ADDITIONAL_DEFAULT_COLUMNS}
					onColumnChange={setColumns}
					rows={activeRows}
					onFilterChange={(f) => setFilters(f)}
				/>
			</Box>

			{/* ── Table ───────────────────────────────────────────────────── */}
			<StyledTable
				columns={columns}
				rows={activeRows}
				filters={filters}
				sx={{ flex: 1 }}
				elevated
				isRowClickCopy={true}
			/>
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(UpcomingParts);
